// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-08-11

import "./ContentManager.less";

import "react-quill/dist/quill.snow.css";

import { Button, Divider, Input } from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import { cloneDeep } from "lodash";
import * as React from "react";

import { CancellationToken } from "lib/api/base";
import { ContentHomeApi } from "lib/api/contentHome";
import { ContentHome, ContentSection } from "lib/data";
import { StateService } from "lib/services";

import { EditableContentSection } from "../components/ContentSection";

interface IContentManagerProps {

}

interface IContentManagerState {
	contentHome: ContentHome | undefined;
	originalContentHome: ContentHome | undefined;
	titleLoading: boolean;
}

export class ContentManager extends React.Component<IContentManagerProps, IContentManagerState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	public constructor(props: IContentManagerProps) {

		super(props);

		this.state = {
			contentHome: undefined,
			originalContentHome: undefined,
			titleLoading: false
		};
	}

	public async componentWillMount() {

		const contentHome = await ContentHomeApi.getContentHome();

		StateService.setCancelableState(() => this.setState({
			contentHome,
			originalContentHome: contentHome
		}), this.apiCancellationToken);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		const className = "content-manager-page";

		// Check if the title actions should be disabled
		const titleActionsDisabled = (
			this.state.contentHome !== undefined &&
			this.state.originalContentHome !== undefined &&
			this.state.contentHome.title === this.state.originalContentHome.title
		);

		return (
			<div className={className}>
				<h3>Content Manager</h3>
				<Divider orientation="left"><h4>Home Title</h4></Divider>
				<div className="title-input-container">
					<Input
						value={this.state.contentHome !== undefined ? this.state.contentHome.title : ""}
						onChange={this.onContentHomeTitleChange}
						addonAfter={(
							<ButtonGroup className="title-action-button-group">
								<Button
									icon="check"
									type="primary"
									disabled={titleActionsDisabled}
									onClick={this.onContentHomeTitleSave}
								/>
								<Button
									icon="close"
									type="danger"
									disabled={titleActionsDisabled}
									onClick={this.onContentHomeTitleCancel}
								/>
							</ButtonGroup>
						)}
					/>
				</div>
				<Divider orientation="left"><h4>Home Sections</h4></Divider>
				{
					this.state.originalContentHome !== undefined ? this.state.originalContentHome.sections.map((contentSection) => (
						<EditableContentSection
							className="editable-content-section-item"
							key={`content-section-${contentSection.id}`}
							contentSection={contentSection}
							onSave={this.onSectionSave}
							onAfterSave={this.onAfterSave}
							onSectionDelete={this.onContentSectionDelete}
						/>
					)) : null
				}
				<Button
					className="add-content-section-button"
					type="primary"
					icon="plus-circle"
					size="large"
					onClick={this.onContentSectionAdd}
				>
					Add Content Section
				</Button>
			</div>
		);
	}

	/**
	 * Updates the state with a new title
	 */
	private onContentHomeTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		const contentHome = cloneDeep(this.state.contentHome);

		if (contentHome !== undefined) {

			contentHome.title = event.target.value;
			StateService.setCancelableState(() => this.setState({contentHome}), this.apiCancellationToken);
		}

	}

	/**
	 * Updates the content home title, and saves the content home
	 */
	private onContentHomeTitleSave = async () => {

		const contentHome = cloneDeep(this.state.originalContentHome);

		if (contentHome !== undefined && this.state.contentHome !== undefined) {

			// update the content home's title and save it
			contentHome.title = this.state.contentHome.title;
			const updatedContentHome = await ContentHomeApi.updateContentHome(contentHome);

			if (updatedContentHome !== undefined) {

				StateService.setCancelableState(() => this.setState({
					originalContentHome: updatedContentHome,
					contentHome: updatedContentHome
				}), this.apiCancellationToken);
			}
		}
	}

	/**
	 * Cancels title edit
	 */
	private onContentHomeTitleCancel = () => {

		StateService.setCancelableState(() => this.setState({
			contentHome: cloneDeep(this.state.originalContentHome)
		}), this.apiCancellationToken);
	}

	/**
	 * Saves a content section
	 */
	private onSectionSave = async (contentSection: ContentSection): Promise<boolean> => {

		const contentHome = cloneDeep(this.state.contentHome);
		if (contentHome !== undefined) {

			// find the matches content section in the content home
			const sectionToUpdateIndex = contentHome.sections.findIndex((section) => section.id === contentSection.id);

			if (sectionToUpdateIndex > -1) {

				// Update the content section, and save the content home
				contentHome.sections[sectionToUpdateIndex] = contentSection;
				const updatedContentHome = await ContentHomeApi.updateContentHome(contentHome);

				return new Promise((res) => {
					StateService.setCancelableState(() => this.setState({
						contentHome: updatedContentHome
					}, () => res(true)), this.apiCancellationToken);
				});
			}
		}

		return false;
	}

	/**
	 * Executes after successful content section save
	 */
	private onAfterSave = () => {

		StateService.setCancelableState(() => this.setState({
			originalContentHome: cloneDeep(this.state.contentHome)
		}), this.apiCancellationToken);
	}

	/**
	 * Adds a new content section to the content home
	 */
	private onContentSectionAdd = async () => {

		const contentHome = cloneDeep(this.state.originalContentHome);

		if (contentHome !== undefined) {

			// create a new content section and save it
			const newContentSection = new ContentSection();
			contentHome.sections.push(newContentSection);

			StateService.setCancelableState(() => this.setState({
				contentHome,
				originalContentHome: contentHome
			}), this.apiCancellationToken);
		}
	}

	/**
	 * Deletes a content section from the content home
	 */
	private onContentSectionDelete = async (contentSection: ContentSection) => {

		const contentHome = cloneDeep(this.state.originalContentHome);

		if (contentHome !== undefined) {

			// find the matches content section in the content home
			const deleteSectionIndex = contentHome.sections.findIndex((section) => section.id === contentSection.id);

			if (deleteSectionIndex > -1) {

				// remove the found section, and save the content home
				contentHome.sections.splice(deleteSectionIndex, 1);
				const updatedContentHome = await ContentHomeApi.updateContentHome(contentHome);
				StateService.setCancelableState(() => this.setState({
					contentHome: updatedContentHome,
					originalContentHome: updatedContentHome
				}), this.apiCancellationToken);
			}
		}
	}
}
